import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ChevronRight, Compass } from 'lucide-react';
import { ExportRealityCheck, type ExportRealityCheckStep, type ExportReasonResource } from '@/components/tools/export-reality-check';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import { useToolAccessGuard } from '@/lib/hooks/useToolAccessGuard';

/**
 * Linked resources woven into specific reason cards. Keyed by reason id.
 * Add app pages or external links here over time — the tool renders them
 * inside the matching reason once the person engages with it.
 */
const REASON_RESOURCES: Record<string, ExportReasonResource[]> = {
  'opportunity-cost': [{
    id: 'trade-opportunities',
    label: 'Browse trade opportunities',
    description: 'Compare export against nearer-term ways to grow.',
    href: '/trade-opportunities'
  }],
  'market-knowledge': [{
    id: 'hubs',
    label: 'Explore support hubs',
    description: 'Find people and organisations who know the market.',
    href: '/hubs'
  }, {
    id: 'ask-community',
    label: 'Ask the community',
    description: 'Turn an assumption into a specific question.',
    href: '/asks'
  }],
  'distribution-strategy': [{
    id: 'directory',
    label: 'Search the directory',
    description: 'Look for partners rather than settling for a catalogue listing.',
    href: '/directory'
  }]
};
const VALID_STEPS: ExportRealityCheckStep[] = ['intro', 'why', 'should', 'ready', 'results'];
export default function ExportRealityCheckPage() {
  const $ = _c(28);
  const router = useRouter();
  const {
    allowed,
    loading
  } = useToolAccessGuard("/tools/export-reality-check");
  const [isEmbed, setIsEmbed] = useState(_temp);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsEmbed(new URLSearchParams(window.location.search).has("embed"));
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== allowed || $[3] !== isEmbed || $[4] !== loading || $[5] !== router) {
    t2 = () => {
      if (!loading && !allowed && !isEmbed) {
        router.replace("/tools");
      }
    };
    t3 = [loading, allowed, isEmbed, router];
    $[2] = allowed;
    $[3] = isEmbed;
    $[4] = loading;
    $[5] = router;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      filters: {
        step: {
          paramName: "step",
          defaultValue: "intro"
        }
      }
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    values,
    handlers,
    isReady
  } = useUrlSync(t4);
  let t5;
  if ($[9] !== values.step) {
    t5 = VALID_STEPS.includes(values.step as ExportRealityCheckStep) ? values.step as ExportRealityCheckStep : "intro";
    $[9] = values.step;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const activeStep = t5;
  let t6;
  if ($[11] !== handlers) {
    t6 = function handleStepChange(next) {
      handlers.step(next);
    };
    $[11] = handlers;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleStepChange = t6;
  if (loading || !allowed && !isEmbed) {
    return null;
  }
  if (isEmbed) {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Head><title>Export Reality Check</title><meta name="description" content="A lean challenge for NZ food & beverage brands: should you export, and are you ready? Weigh the real reasons not to." /></Head>;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <EmbedHeightReporter />;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <>{t7}<div className="bg-white px-4 py-6">{t8}<div id="export-reality-check-embed"><ExportRealityCheck reasonResources={REASON_RESOURCES} /></div></div></>;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    return t9;
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Head><title>Export Reality Check | Sizzle</title><meta name="description" content={"Pressure-test your export decision. A lean challenge for NZ food & beverage brands on whether you should export \u2014 and whether you're ready."} /></Head>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-teal-500/20 blur-3xl" />;
    t9 = <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-emerald-600/20 blur-3xl" />;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <nav className="mb-8 flex items-center gap-1.5 text-sm text-gray-400"><Link href="/tools" className="cursor-pointer transition-colors hover:text-white">Tools</Link><ChevronRight className="h-3.5 w-3.5" /><span className="font-medium text-white">Export Reality Check</span></nav>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <section className="relative overflow-hidden border-b border-gray-200 bg-gray-950 text-white">{t8}{t9}<div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">{t10}<div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px] lg:items-end"><div className="max-w-3xl"><h1 className="mt-5 text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl">An Export Reality Check for NZ Brands</h1><p className="mt-5 max-w-2xl text-base leading-relaxed text-gray-300 sm:text-lg">{"Export is often positioned as the obvious next step. For many NZ food & beverage brands it isn't \u2014 or not yet. This tool walks the common reasons it doesn't work out, so you can pressure-test the decision before committing."}</p></div></div></div></section>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <section className="border-b border-gray-100 bg-gradient-to-r from-teal-50 via-white to-emerald-50"><div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"><p className="max-w-4xl text-sm leading-relaxed text-gray-700"><strong className="text-gray-950">This is a challenge, not a verdict.</strong> The signal reflects your own answers. Use it to think more clearly about whether you're ready to export, and what you should prove before investing time and money in a new market.</p></div></section>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== activeStep || $[23] !== handleStepChange || $[24] !== isReady) {
    t13 = isReady && <ExportRealityCheck reasonResources={REASON_RESOURCES} activeStep={activeStep} onStepChange={handleStepChange} reasonCardProps={{
      alwaysShowResources: true
    }} />;
    $[22] = activeStep;
    $[23] = handleStepChange;
    $[24] = isReady;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <>{t7}<main className="min-h-screen bg-white">{t11}{t12}<section id="reality-check" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-10 sm:px-6 sm:py-14 lg:px-8">{t13}</section></main></>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}
function _temp() {
  if (typeof window === "undefined") {
    return false;
  }
  return new URLSearchParams(window.location.search).has("embed");
}
function EmbedHeightReporter() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
  return null;
}
function _temp2() {
  if (window.self === window.top) {
    return;
  }
  const content = document.getElementById("export-reality-check-embed");
  if (!content) {
    return;
  }
  let lastHeight = 0;
  const reportHeight = function reportHeight() {
    const height = content.offsetHeight + 48;
    if (Math.abs(height - lastHeight) <= 5) {
      return;
    }
    lastHeight = height;
    window.parent.postMessage({
      type: "embed-resize",
      height
    }, "*");
  };
  const observer = new ResizeObserver(reportHeight);
  observer.observe(content);
  reportHeight();
  return () => observer.disconnect();
}